import {AxiosError} from "axios";
import {AtSign as MTAIcon, Bin as DeleteIcon} from "iconoir-react";
import {useEffect, useState} from 'react'
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {type MailDetails, MailDetailsWithMIME, MPAMailQueueApi} from "../api/admin/generated";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {InvertSelection} from "../common/InvertSelection";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {Truncate} from "../common/Truncate";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {DateUtils} from "../utils/DateUtils";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {MPAViewMailDialog} from "./MPAViewMailDialog";

/*
 * REST api for accessing the back-end
 */
const mailQueueApi = new MPAMailQueueApi(ApiConfig.config());

type OnClick = () => void;

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
    onClick? : OnClick
}

const Column = ({value, children, className, maxLength = 60, onClick}: ColumnProps) => {
    return (
        <td onClick={onClick} className={"user-select-all cursor-pointer " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const MPAQueue = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * Represents the current location object in a React Router application.
     */
    const location = useLocation()
    /**
     * The mail queue names
     */
    const [queueNames, setQueueNames] = useState<string[]>();
    /**
     * The selected mail queue name
     */
    const [selectedQueueName, setSelectedQueueName] = useState<string>(location?.state?.selectedQueueName || undefined);
    /**
     * The mail queue items
     */
    const [queueItems, setQueueItems] = useState<MailDetails[]>([]);
    /**
     * The total number of MPA queue items
     */
    const [queueSize, setQueueSize] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected queue item
     */
    const [selectedQueueItems, setSelectedQueueItems] = useState<MailDetails[]>([]);
    /**
     * The downloaded Mail with MIME contents
     */
    const [mailWithMime, setMailWithMime] = useState<MailDetailsWithMIME>();
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "mail dialog" modal dialog will be shown
     */
    const [showMailDialog, setShowMailDialog] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        logger.debug("Load queue names")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailQueueApi.mailQueueControllerGetQueueNames()
            .then(response =>
            {
                const queueNames = response.data;

                setQueueNames(queueNames);

                if (selectedQueueName === undefined && queueNames !== undefined && queueNames.length > 0) {
                    setSelectedQueueName(queueNames[0])
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue names", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        selectedQueueName,
        t
    ]);


    useEffect(() =>
    {
        if (selectedQueueName === undefined) {
            return;
        }

        logger.debug("Load queue size")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailQueueApi.mailQueueControllerGetQueueSize(selectedQueueName)
            .then(response => {
                setQueueSize(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue size", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        selectedQueueName,
        t
    ]);

    useEffect(() =>
    {
        if (selectedQueueName === undefined) {
            return;
        }

        logger.debug("Load queue items")

        // reset the state when we load new data
        setSelectedQueueItems([]);
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailQueueApi.mailQueueControllerGetQueuedMails(selectedQueueName, (currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setQueueItems(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue list", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        selectedQueueName,
        t
    ]);

    const onChangeSelect = (checked : boolean, queueItem : MailDetails) =>
    {
        setSelectedQueueItems(checked ?
            [...selectedQueueItems, queueItem] :
            selectedQueueItems.filter(q => q.name !== queueItem.name)
        );
    }

    const onClickMailname = (queueItem : MailDetails) =>
    {
        if (selectedQueueName === undefined) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailQueueApi.mailQueueControllerGetQueuedMail(selectedQueueName, queueItem.name, true)
            .then((result) => {
                setMailWithMime(result.data)
                setShowMailDialog(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error downloading queue item", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }

    const invertSelection = () => {
        setSelectedQueueItems([...queueItems].filter(q => !selectedQueueItems.includes(q)));
    }

    const deleteSelected = () =>
    {
        if (selectedQueueName === undefined) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mailQueueApi.mailQueueControllerDeleteQueuedMails(selectedQueueName, selectedQueueItems.map(q => q.name))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedQueueItems([]);
            })
    }

    /**
     * Is called when the View Mail Dialog is closed
     */
    const onMPAViewMailDialogClose = () => {
        setShowMailDialog(false);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("mta-queue.are-you-sure-delete-emails")}
                           confirmButtonTitle={t("common.confirm")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showMailDialog && mailWithMime !== undefined && <MPAViewMailDialog mailWithMIME={mailWithMime} onClose={onMPAViewMailDialogClose} />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("mpa-queue.title")} icon={<MTAIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex justify-content-start">
                        <Form.Select value={selectedQueueName} onChange={(e) => setSelectedQueueName(e.target.value)}>
                            {queueNames?.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Form.Select>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        <thead className="sticky-top">
                        <tr className="text-nowrap">
                            {selectedQueueItems.length === 0 ?
                                <>
                                <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                <th>{t('mpa-table.name')}</th>
                                <th>{t('mpa-table.recipients')}</th>
                                <th>{t('mpa-table.sender')}</th>
                                <th>{t('mpa-table.state')}</th>
                                <th>{t('mpa-table.remote-host')}</th>
                                <th>{t('mpa-table.remote-address')}</th>
                                <th>{t('mpa-table.error-message')}</th>
                                <th>{t('mpa-table.message-size')}</th>
                                <th>{t('mpa-table.last-updated')}</th>
                                </>
                                :
                                <th colSpan={10}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedQueueItems([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedQueueItems.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                <DeleteIcon className="text-danger"/> {t("mpa-queue.delete-selected")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            }
                        </tr>
                        </thead>
                        <tbody>
                        {queueItems?.map((q: MailDetails) => (
                            <tr key={q.name} className="text-nowrap">
                                <td className="td-w-min">
                                    <Form.Check
                                        type="checkbox"
                                        className="me-0"
                                        checked={selectedQueueItems.find((sitem) =>  sitem.name === q.name) !== undefined}
                                        onChange={(e) => onChangeSelect(e.target.checked, q)}
                                        inline
                                    />
                                </td>
                                <Column value={q.name} onClick={() => onClickMailname(q)} />
                                <Column value={q.recipients.join(", ")} onClick={() => onClickMailname(q)} />
                                <Column value={q.sender} onClick={() => onClickMailname(q)} />
                                <Column value={q.state} onClick={() => onClickMailname(q)} />
                                <Column value={q.remoteHost} onClick={() => onClickMailname(q)} />
                                <Column value={q.remoteAddr} onClick={() => onClickMailname(q)} />
                                <Column value={q.errorMessage} onClick={() => onClickMailname(q)} />
                                <Column value={`${q.messageSize}`} onClick={() => onClickMailname(q)} />
                                <Column value={DateUtils.timestampToDateStringShort(q.lastUpdated)} onClick={() => onClickMailname(q)} />
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {queueSize} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={queueSize}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
